import * as tslib_1 from "tslib";
import React from 'react';
import Waypoint from 'react-waypoint';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import Flag from '../redux/flags/Flag';
import { Url } from '@wix/da-url';
import Comment from '../Comment';
import CommentBox from '../CommentBox';
import LoadingIndicator from '../LoadingIndicator';
import Button from '../Button';
import StandardDropdown from '../Dropdown/Preset/StandardDropdown';
import { flatThreadToTree, getCommentBoxId, updateCommentOffset, isActiveThread, } from './helpers';
import { scrollIntoViewWithHeader } from '../utils/scrollIntoViewWithHeader';
import { withMobileContext } from '../Context';
import ThreadComment from './_partials/ThreadComment';
import { commentRestoreFlag } from './constants';
import ThreadPagination from './_partials/ThreadPagination';
import s from './CommentThread.scss';
var CommentThread = /** @class */ (function (_super) {
    tslib_1.__extends(CommentThread, _super);
    function CommentThread() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.maxLevel = 3;
        _this.threadStartRef = React.createRef();
        _this.lastCommentRef = React.createRef();
        _this.needsToScroll = false;
        _this.onClickLoadMore = function () {
            var _a = _this.props, fetchMore = _a.fetchMore, streamId = _a.streamId;
            return fetchMore(streamId);
        };
        _this.onClickLoadPrevious = function () {
            var _a = _this.props, fetchPrevious = _a.fetchPrevious, streamId = _a.streamId;
            fetchPrevious(streamId);
        };
        _this.onOrderChanged = function (value) {
            var _a = _this.props, onOrderChanged = _a.onOrderChanged, streamId = _a.streamId;
            onOrderChanged && onOrderChanged(streamId, value);
        };
        _this.onThreadUpdated = function (result) {
            var _a = _this.props, onThreadUpdated = _a.onThreadUpdated, streamId = _a.streamId;
            onThreadUpdated && onThreadUpdated(streamId, result);
        };
        _this.openReplies = function (commentId, comment) {
            var _a = _this.props, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, onOpenCommentModal = _a.onOpenCommentModal;
            // If we already in iframe just redirect
            if (window.self !== window.top) {
                window.location.assign(Url.commentLink(commentableTypeId, commentableItemId, commentId, undefined, true));
                return;
            }
            onOpenCommentModal(commentableTypeId, commentableItemId, commentId, comment);
        };
        _this.closeReplies = function () {
            _this.setState({ openReplyModal: false });
        };
        _this.scrollToLatestComment = function () {
            if (!_this.lastCommentRef || !_this.lastCommentRef.current) {
                _this.needsToScroll = true;
                return;
            }
            scrollIntoViewWithHeader(_this.lastCommentRef.current, _this.props.isMobile);
        };
        _this.renderRecursiveComment = function (comment, index) {
            var _a = _this.props, currentUser = _a.currentUser, lastInsertedCommentId = _a.lastInsertedCommentId, streamId = _a.streamId, commentClassName = _a.commentClassName, commentBodyClassName = _a.commentBodyClassName, enableOffsetTracking = _a.enableOffsetTracking, t = _a.t, canPostComment = _a.canPostComment;
            var commentId = comment.commentId, indent = comment.indent, isLeafComment = comment.isLeafComment, children = comment.children, replies = comment.replies;
            var isLastComment = lastInsertedCommentId && lastInsertedCommentId === commentId;
            return (React.createElement(React.Fragment, { key: "comment-wrap-" + index },
                React.createElement("div", { className: s["indent-" + indent], ref: isLastComment ? _this.lastCommentRef : undefined },
                    React.createElement(Flag, { condition: commentRestoreFlag, render: function () { return (React.createElement(ThreadComment, { key: "comment-" + commentId, streamId: streamId, indent: indent, currentUser: currentUser, comment: comment, className: commentClassName, bodyClassName: commentBodyClassName, enableOffsetTracking: enableOffsetTracking, canPostComment: !!canPostComment })); }, fallbackRender: function () { return (React.createElement(Comment, { key: "comment-" + commentId, indent: indent, currentUser: currentUser, comment: comment, className: commentClassName, bodyClassName: commentBodyClassName, onReplyPosted: _this.onThreadUpdated, onCommentUpdated: _this.onThreadUpdated, canPostComment: !!canPostComment })); } })),
                children && children.map(_this.renderRecursiveComment),
                isLeafComment && replies > 0 && (React.createElement("div", { key: "comment-" + index, className: s["indent-" + indent] },
                    React.createElement("a", { onClick: _this.openReplies.bind(_this, commentId, comment), className: s['replies'] },
                        t('commentthread.control.view.replies', {
                            count: replies + ((children && children.length) || 0),
                        }),
                        ' ')))));
        };
        return _this;
    }
    CommentThread.prototype.componentDidMount = function () {
        var _a = this.props, isInitialized = _a.isInitialized, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, isMobile = _a.isMobile;
        if (!isInitialized) {
            this.onStreamIdChanged();
        }
        if (isActiveThread(commentableTypeId, commentableItemId) &&
            this.threadStartRef.current) {
            // TODO: this will need perspective context if used in profiles
            scrollIntoViewWithHeader(this.threadStartRef.current, isMobile);
        }
    };
    CommentThread.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, lastInsertedCommentId = _a.lastInsertedCommentId, streamId = _a.streamId, isInitialized = _a.isInitialized;
        if (prevProps.streamId !== streamId && !isInitialized) {
            this.onStreamIdChanged();
        }
        // there is some weird hiccup in the react lifecycle atm
        // where the refs dont correctly get set on the first update,
        // there is an immediate second render that sets the ref
        // so we do a little shenanigan here to try the scrolling again
        if ((lastInsertedCommentId &&
            prevProps.lastInsertedCommentId !== lastInsertedCommentId) ||
            this.needsToScroll) {
            this.needsToScroll = false;
            this.scrollToLatestComment();
        }
    };
    CommentThread.prototype.onStreamIdChanged = function () {
        var _a = this.props, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, parentId = _a.parentId, order = _a.order, streamId = _a.streamId, onStreamIdChanged = _a.onStreamIdChanged, _b = _a.allowSelfInit, allowSelfInit = _b === void 0 ? true : _b;
        if (!allowSelfInit) {
            return;
        }
        onStreamIdChanged({
            commentableItemId: commentableItemId,
            commentableTypeId: commentableTypeId,
            parentId: parentId,
            order: order,
            streamId: streamId,
        });
    };
    CommentThread.prototype.render = function () {
        var _this = this;
        return (React.createElement(Flag, { condition: commentRestoreFlag, render: function () { return (React.createElement(Waypoint, { onLeave: function () { return updateCommentOffset(); } }, _this.renderThread())); }, fallbackRender: function () { return _this.renderThread(); } }));
    };
    CommentThread.prototype.renderThread = function () {
        var _this = this;
        var _a = this.props, className = _a.className, slim = _a.slim, items = _a.items, isMobile = _a.isMobile, streamId = _a.streamId, _b = _a.hasMore, hasMore = _b === void 0 ? false : _b, _c = _a.hasLess, hasLess = _c === void 0 ? false : _c, isInfinite = _a.isInfinite, _d = _a.isFetching, isFetching = _d === void 0 ? false : _d, t = _a.t;
        var postbox = this.props.postbox;
        if (postbox === 'both') {
            if (!items || items.length < 6) {
                postbox = 'top';
            }
        }
        return (React.createElement("div", { ref: this.threadStartRef, className: classnames(s['root'], className, (slim || isMobile) && s['slim']) },
            (postbox === 'both' || postbox === 'top') && this.renderPostbox('top'),
            React.createElement(Flag, { condition: commentRestoreFlag },
                React.createElement(ThreadPagination, { hasMore: hasLess, isFetching: isFetching, streamId: streamId, onLoadMore: this.onClickLoadPrevious }, t('commentthread.control.loadprev'))),
            this.renderThreadStack(),
            this.renderLoader(),
            React.createElement(Flag, { condition: commentRestoreFlag, render: function () { return (React.createElement(ThreadPagination, { hasMore: hasMore, isInfinite: isInfinite, isFetching: isFetching, streamId: streamId, onLoadMore: _this.onClickLoadMore }, t('commentthread.control.loadmore'))); }, fallbackRender: function () { return _this.renderPagination(); } }),
            (postbox === 'both' || postbox === 'bottom') &&
                this.renderPostbox('bottom')));
    };
    CommentThread.prototype.renderThreadStack = function () {
        var items = this.props.items;
        if (!items || items.length === 0) {
            return null;
        }
        var renderItems = flatThreadToTree(items, this.maxLevel);
        return (renderItems && (React.createElement(React.Fragment, null,
            this.renderStatsAndControls(),
            renderItems.map(this.renderRecursiveComment))));
    };
    CommentThread.prototype.renderPostbox = function (position) {
        var _a = this.props, currentUser = _a.currentUser, commentableTypeId = _a.commentableTypeId, commentableItemId = _a.commentableItemId, parentComment = _a.parentComment, canPostComment = _a.canPostComment;
        return (React.createElement("div", { className: s['commentbox'] },
            React.createElement(CommentBox, { id: getCommentBoxId(commentableTypeId, commentableItemId, position), commentableType: commentableTypeId, commentableItemId: commentableItemId, replyComment: parentComment, user: currentUser, onPublishSuccess: this.onThreadUpdated, tabIndexBase: position === 'top' ? 0 : 10, canPostComment: !!canPostComment })));
    };
    CommentThread.prototype.renderLoader = function () {
        var isFetching = this.props.isFetching;
        return isFetching && React.createElement(LoadingIndicator, { view: "llama" });
    };
    CommentThread.prototype.renderPagination = function () {
        var _a = this.props, t = _a.t, hasMore = _a.hasMore, isFetching = _a.isFetching, isInfinite = _a.isInfinite;
        return (hasMore &&
            (isInfinite ? (React.createElement(Waypoint, { topOffset: 100, onEnter: this.onClickLoadMore })) : (React.createElement("div", { className: s['loadmore-container'] },
                React.createElement(Button, { view: 'b8', onClick: this.onClickLoadMore, disabled: isFetching, className: s['loadmore'] }, t('commentthread.control.loadmore'))))));
    };
    CommentThread.prototype.renderStatsAndControls = function () {
        var _a = this.props, t = _a.t, total = _a.total, _b = _a.statsAndControls, statsAndControls = _b === void 0 ? 'on' : _b;
        if (statsAndControls === 'off') {
            return;
        }
        return (React.createElement("div", { className: s['controls'] },
            ['on', 'title'].includes(statsAndControls) && (React.createElement("div", { className: s['total-stat'] },
                t('commentthread.control.comments'),
                " ",
                total && "(" + total + ")")),
            ['on', 'order'].includes(statsAndControls) && (React.createElement("div", { className: s['order-select'] }, this.renderOrder()))));
    };
    CommentThread.prototype.renderOrder = function () {
        var _a = this.props, t = _a.t, order = _a.order, isLoggedIn = _a.isLoggedIn;
        if (!isLoggedIn) {
            return null;
        }
        var items = [
            {
                label: t('commentthread.control.newest'),
                value: 'newest',
            },
            {
                label: t('commentthread.control.oldest'),
                value: 'oldest',
            },
        ];
        return (React.createElement(StandardDropdown, { toggleClassName: s['order-select-toggle'], selectedValue: order, items: items, onSelect: this.onOrderChanged }));
    };
    return CommentThread;
}(React.Component));
export { CommentThread };
export default withMobileContext(withTranslation()(CommentThread));
//# sourceMappingURL=CommentThread.js.map